import { getDbSupabase } from './supabase';
import { formatSupabaseError } from './formatSupabaseError';
import {
  createPricingUpdateItemRow,
  EMPTY_PRICING_UPDATE_FORM,
  type ApplicationSubmission,
  type PricingUpdateAttachmentRef,
  type PricingUpdateFormData,
  type PricingUpdateItemRow,
} from '../types/applicationForm';

const TABLE = 'pricing_update_submissions';
const FORM_SLUG = 'pricing-update' as const;
const BUCKET = 'pricing-update-attachments';

function withPufSlug(row: ApplicationSubmission): ApplicationSubmission {
  return {
    ...row,
    form_slug: FORM_SLUG,
    form_data: normalizePricingUpdateFormData(row.form_data),
  };
}

function normalizeAttachment(raw: unknown): PricingUpdateAttachmentRef | null {
  if (!raw || typeof raw !== 'object') return null;
  const data = raw as Partial<PricingUpdateAttachmentRef>;
  const path = String(data.path ?? '').trim();
  if (!path) return null;
  const fileName = String(data.fileName ?? '').trim() || path.split('/').pop() || 'attachment';
  return {
    path,
    fileName,
    size: typeof data.size === 'number' ? data.size : undefined,
    mime: typeof data.mime === 'string' ? data.mime : undefined,
  };
}

/** Coerce Excel/import date strings into YYYY-MM-DD for <input type="date">. */
function normalizeItemDate(raw: unknown): string {
  const value = String(raw ?? '').trim();
  if (!value) return '';
  const isoPrefix = value.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoPrefix) return isoPrefix[1];
  const slash = value.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
  if (slash) {
    return `${slash[3]}-${slash[2].padStart(2, '0')}-${slash[1].padStart(2, '0')}`;
  }
  return '';
}

/**
 * Typeahead labels look like "CODE — Description". If a user never picks from the
 * list, that whole string can land in item_no — split it back apart for display/save.
 * Item codes may contain ASCII hyphens (e.g. CU-SVFK95K-R), so only treat spaced
 * separators or em/en dashes as the code/description split.
 */
export function splitPufItemCodeAndDescription(
  itemNo: string,
  itemDescription = '',
): { item_no: string; item_description: string } {
  const codeRaw = String(itemNo ?? '').trim();
  const descRaw = String(itemDescription ?? '').trim();
  if (!codeRaw) return { item_no: '', item_description: descRaw };

  // "CODE — Desc" / "CODE – Desc" / "CODE - Desc" (spaces required for ASCII hyphen)
  const spaced = codeRaw.match(/^(\S+)\s+[—–\-]\s+(.+)$/u);
  if (spaced) {
    const code = spaced[1].trim();
    const fromLabel = spaced[2].trim();
    if (code) {
      return { item_no: code, item_description: descRaw || fromLabel };
    }
  }

  // "CODE—Desc" / "CODE–Desc" (em/en dash, no spaces; never ASCII hyphen alone)
  const compact = codeRaw.match(/^(\S+?)[—–]\s*(.+)$/u);
  if (compact) {
    const code = compact[1].trim();
    const fromLabel = compact[2].trim();
    if (code) {
      return { item_no: code, item_description: descRaw || fromLabel };
    }
  }

  return { item_no: codeRaw, item_description: descRaw };
}

function normalizeItem(raw: unknown): PricingUpdateItemRow {
  const data = (raw && typeof raw === 'object' ? raw : {}) as Partial<PricingUpdateItemRow>;
  const split = splitPufItemCodeAndDescription(
    String(data.item_no ?? ''),
    String(data.item_description ?? ''),
  );
  return createPricingUpdateItemRow({
    id: String(data.id ?? '').trim() || undefined,
    item_no: split.item_no,
    item_description: split.item_description,
    sales_uom: String(data.sales_uom ?? ''),
    sales_price: String(data.sales_price ?? ''),
    moq: data.moq == null || data.moq === '' ? '' : String(data.moq),
    effective_date: normalizeItemDate(data.effective_date),
    ending_date: normalizeItemDate(data.ending_date),
  });
}

export function normalizePricingUpdateFormData(raw: unknown): PricingUpdateFormData {
  const data = (raw && typeof raw === 'object' ? raw : {}) as Partial<PricingUpdateFormData>;
  const itemsRaw = Array.isArray(data.items) ? data.items : [];
  const items = itemsRaw.length > 0 ? itemsRaw.map(normalizeItem) : [createPricingUpdateItemRow()];
  const requestorAttachments = Array.isArray(data.requestor_attachments)
    ? data.requestor_attachments.map(normalizeAttachment).filter(Boolean)
    : [];
  const authorisedAttachments = Array.isArray(data.authorised_attachments)
    ? data.authorised_attachments.map(normalizeAttachment).filter(Boolean)
    : [];
  return {
    ...EMPTY_PRICING_UPDATE_FORM,
    customer_no: String(data.customer_no ?? ''),
    customer_name: String(data.customer_name ?? ''),
    customer_price_group: String(data.customer_price_group ?? ''),
    reason_requestor: String(data.reason_requestor ?? ''),
    reason_decision: String(data.reason_decision ?? ''),
    requestor_attachments: requestorAttachments as PricingUpdateAttachmentRef[],
    authorised_attachments: authorisedAttachments as PricingUpdateAttachmentRef[],
    items,
  };
}

export function companyNameFromPricingUpdate(form: PricingUpdateFormData): string {
  return form.customer_name.trim() || form.customer_no.trim() || 'Pricing Update';
}

export function validatePricingUpdateRequestor(form: PricingUpdateFormData): string | null {
  if (!form.customer_no.trim()) return 'Select a customer number.';
  if (!form.customer_name.trim()) {
    return 'Select a customer from the list so the name fills in.';
  }
  if (form.items.length === 0) return 'Add at least one item.';
  const emptyIndexes = form.items
    .map((row, i) => (!row.item_no.trim() ? i + 1 : null))
    .filter((n): n is number => n != null);
  if (emptyIndexes.length > 0) {
    const which =
      emptyIndexes.length === 1
        ? `Item row ${emptyIndexes[0]}`
        : `Item rows ${emptyIndexes.join(', ')}`;
    return `${which} is empty. Enter an item or remove the row before submit.`;
  }
  for (let i = 0; i < form.items.length; i += 1) {
    const row = form.items[i];
    const label = `Item row ${i + 1}`;
    const split = splitPufItemCodeAndDescription(row.item_no, row.item_description);
    if (!split.item_description) {
      return `${label}: select ${split.item_no || 'the item'} from the list so description fills in.`;
    }
  }
  if (!form.reason_requestor.trim()) return 'Enter the requestor reason.';
  return null;
}

/** Item/price checks for Submit to Approver. Does not require reason of the decision. */
export function validatePricingUpdateAuthorised(form: PricingUpdateFormData): string | null {
  if (form.items.length === 0) return 'At least one item is required.';
  const emptyIndexes = form.items
    .map((row, i) => (!row.item_no.trim() ? i + 1 : null))
    .filter((n): n is number => n != null);
  if (emptyIndexes.length > 0) {
    const which =
      emptyIndexes.length === 1
        ? `Item row ${emptyIndexes[0]}`
        : `Item rows ${emptyIndexes.join(', ')}`;
    return `${which} is empty. Enter an item or remove the row before submit.`;
  }
  for (let i = 0; i < form.items.length; i += 1) {
    const row = form.items[i];
    const label = `Item row ${i + 1}`;
    const split = splitPufItemCodeAndDescription(row.item_no, row.item_description);
    if (!split.item_description) {
      return `${label}: select ${split.item_no || 'the item'} from the list so description fills in.`;
    }
    if (!row.sales_price.trim()) return `${label}: enter sales price.`;
    if (!row.effective_date.trim()) return `${label}: enter effective date.`;
  }
  return null;
}

/** Required when Approve / Reject / Return — write it in Reason of the decision (not a popup). */
export function requirePricingUpdateDecisionReason(
  form: PricingUpdateFormData,
): string | null {
  if (!form.reason_decision.trim()) {
    return 'Enter the reason of the decision before approving or rejecting.';
  }
  return null;
}

function mapRpcRow(data: unknown): ApplicationSubmission {
  return withPufSlug(data as ApplicationSubmission);
}

export async function fetchPricingUpdateSubmissions(): Promise<ApplicationSubmission[]> {
  const db = getDbSupabase('forms');
  const { data, error } = await db
    .from(TABLE)
    .select('*')
    .order('submitted_at', { ascending: false });
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []).map((row) => withPufSlug(row as ApplicationSubmission));
}

export async function fetchPricingUpdateSubmission(id: string): Promise<ApplicationSubmission> {
  const db = getDbSupabase('forms');
  const { data, error } = await db.from(TABLE).select('*').eq('id', id).maybeSingle();
  if (error) throw new Error(formatSupabaseError(error));
  if (!data) throw new Error('Submission not found.');
  return withPufSlug(data as ApplicationSubmission);
}

export async function submitPricingUpdate(
  formData: PricingUpdateFormData,
  submittedBy?: string,
  submitterEmail?: string,
): Promise<ApplicationSubmission> {
  const db = getDbSupabase('forms');
  const payload = normalizePricingUpdateFormData(formData);
  payload.items = payload.items.filter((row) => row.item_no.trim());
  const { data, error } = await db.rpc('submit_pricing_update', {
    p_company_name: companyNameFromPricingUpdate(payload),
    p_form_data: payload,
    p_submitted_by: submittedBy ?? null,
    p_submitter_email: submitterEmail ?? null,
  });
  if (error) throw new Error(formatSupabaseError(error));
  return mapRpcRow(data);
}

export async function startPricingUpdateProcessing(id: string): Promise<ApplicationSubmission> {
  const db = getDbSupabase('forms');
  const { data, error } = await db.rpc('pricing_update_start_processing', { p_id: id });
  if (error) throw new Error(formatSupabaseError(error));
  return mapRpcRow(data);
}

export async function updatePricingUpdateAuthorised(
  id: string,
  formData: PricingUpdateFormData,
): Promise<ApplicationSubmission> {
  const db = getDbSupabase('forms');
  const payload = normalizePricingUpdateFormData(formData);
  const { data, error } = await db.rpc('update_pricing_update_authorised', {
    p_id: id,
    p_form_data: payload,
  });
  if (error) throw new Error(formatSupabaseError(error));
  return mapRpcRow(data);
}

export async function submitPricingUpdateForApproval(
  id: string,
  formData?: PricingUpdateFormData,
): Promise<ApplicationSubmission> {
  const db = getDbSupabase('forms');
  const { data, error } = await db.rpc('submit_pricing_update_for_approval', {
    p_id: id,
    p_form_data: formData ? normalizePricingUpdateFormData(formData) : null,
  });
  if (error) throw new Error(formatSupabaseError(error));
  return mapRpcRow(data);
}

export async function rejectPricingUpdate(
  id: string,
  reason?: string,
  formData?: PricingUpdateFormData,
): Promise<ApplicationSubmission> {
  const db = getDbSupabase('forms');
  const payload = formData ? normalizePricingUpdateFormData(formData) : null;
  const { data, error } = await db.rpc('reject_pricing_update', {
    p_id: id,
    p_reason: reason ?? null,
    p_form_data: payload,
  });
  if (error) throw new Error(formatSupabaseError(error));
  return mapRpcRow(data);
}

export async function approvePricingUpdate(
  id: string,
  formData?: PricingUpdateFormData,
): Promise<ApplicationSubmission> {
  const db = getDbSupabase('forms');
  const payload = formData ? normalizePricingUpdateFormData(formData) : null;
  const { data, error } = await db.rpc('approve_pricing_update', {
    p_id: id,
    p_form_data: payload,
  });
  if (error) throw new Error(formatSupabaseError(error));
  return mapRpcRow(data);
}

export async function uploadPricingUpdateAttachment(
  submissionId: string,
  kind: 'requestor' | 'authorised',
  file: File,
): Promise<PricingUpdateAttachmentRef> {
  const db = getDbSupabase('forms');
  const safeName = file.name.replace(/[^\w.\-()+ ]+/g, '_').slice(0, 120) || kind;
  const path = `${submissionId}/${kind}/${Date.now()}_${safeName}`;
  const { error } = await db.storage.from(BUCKET).upload(path, file, {
    upsert: false,
    contentType: file.type || undefined,
  });
  if (error) throw new Error(error.message || 'Failed to upload attachment.');
  return {
    path,
    fileName: file.name,
    size: file.size,
    mime: file.type || undefined,
  };
}

export async function getPricingUpdateAttachmentSignedUrl(path: string): Promise<string> {
  const db = getDbSupabase('forms');
  const { data, error } = await db.storage.from(BUCKET).createSignedUrl(path, 60 * 30);
  if (error) throw new Error(error.message || 'Failed to open attachment.');
  if (!data?.signedUrl) throw new Error('Failed to open attachment.');
  return data.signedUrl;
}

/** After submit: upload requestor files, then patch attachment metadata on the new row. */
export async function patchPricingUpdateRequestorAttachments(
  id: string,
  formData: PricingUpdateFormData,
): Promise<ApplicationSubmission> {
  const db = getDbSupabase('forms');
  const payload = normalizePricingUpdateFormData(formData);
  const { data, error } = await db.rpc('patch_pricing_update_requestor_attachments', {
    p_id: id,
    p_form_data: payload,
  });
  if (error) throw new Error(formatSupabaseError(error));
  return mapRpcRow(data);
}

const PUF_SG_TZ = 'Asia/Singapore';

/** `YYYY-MM` from `2026/PUF/10/001` (or empty if not parseable). */
export function pufYearMonthFromApplicationNo(applicationNo: string): string | null {
  const m = /^(\d{4})\/PUF\/(\d{1,2})\//i.exec(applicationNo.trim());
  if (!m) return null;
  return `${m[1]}-${m[2].padStart(2, '0')}`;
}

export function currentPufYearMonth(date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: PUF_SG_TZ,
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(date);
  const y = parts.find((p) => p.type === 'year')?.value ?? '2026';
  const m = parts.find((p) => p.type === 'month')?.value ?? '01';
  return `${y}-${m}`;
}
